import { useCallback, useContext, useEffect, useState } from 'react';

import { message, Typography } from 'antd';
import dayjs from 'dayjs';

import { Insured } from './components';
import {
  AdditionalDataSection,
  ClientDataSection,
  PaymentMethodSection,
  QuotationStepButtons,
  ValiditySection,
} from '../../../../../shared';

import QuotationService from '../../../../../../../../../shared/services/quotation';

import { AuthContext } from '../../../../../../../../../shared/contexts';
import {
  GenericDataFormContext,
  QuotationInfoContext,
} from '../../../../../shared/contexts';
import { StepFourFormContext } from '../../contexts/StepFourForm';
import { StepTwoFormContext } from '../../../StepTwo/contexts/StepTwoForm';
import { WizardDataFormContext } from '../../../../Wizard/contexts/WizardDataForm/WizardDataFormContext';

import { displayErrorMessage, formattedDateVigency } from '../../../../../../../../../shared/utils';

import {
  AseguradosVida,
  DECISION_TYPE,
  InsertInsuredPolicyholder,
  SendPaymentMethodRequest,
  SendProposalRequest,
} from '../../../../../../../../../shared/interfaces';
import { QuotationStep } from '../../../../../shared/interfaces';

import { ContentSection, RowSize } from '../../../../../../../../../shared/components';
import { StepThreeFormContext } from '../../../StepThree/contexts/StepThreeForm';
import { DocumentUploadServiceRequestParams } from '../../../../../../../../../shared/interfaces/services/post-send-claim-documents-service';
import { DocumentClass } from '../../../../../../../../../shared/interfaces/enums/document-class-enum';
import ClaimService from '../../../../../../../../../shared/services/claim';
import { getFileExtension } from '../../../../../../../../../shared/utils/fileTypes';
import { fileToBase64 } from '../../../../../../../../../shared/utils/fillToBase64';

export const FormIssue = () => {
  const {
    additionalDataSectionForm,
    clientDataSectionForm,
    genderList,
    isLoadingGenderList,
    listCreditCardInsured,
    paymentMethodFormValues,
    paymentMethodSectionForm,
    setAdditionalDataFormValues,
    setCanShowSpinner,
    setclientDataFormValues,
    setPaymentMethodFormValues,
  } = useContext(GenericDataFormContext);
  const authContext = useContext(AuthContext);
  const {
    errorCreditCardMessage,
    methodSelectedFromStepThree,
    setErrorCreditCardMessage,
    setMethodSelectedFromStepThree,
    validitySectionForm,
  } = useContext(StepFourFormContext);
  const { quotationForm } = useContext(WizardDataFormContext);
  const {
    quotationInfo,
    setCurrentStep,
    setCanShowConfirmIssue,
    setQuotationInfo,
  } = useContext(QuotationInfoContext);
  const { stepThreeForm } = useContext(StepThreeFormContext);
  const { stepTwoForm } = useContext(StepTwoFormContext);

  const isPersonalDomestic = () => {
    const personalDomesticSelected =
      stepTwoForm?.getFieldValue('personalDomestic');

    return personalDomesticSelected === DECISION_TYPE.YES;
  };

  const [insuredList, setInsuredList] = useState<any[]>([]);
  const [uploadFile, setUploadFile] = useState<Blob>();
  const codeMethodPayment = methodSelectedFromStepThree?.codigoFormaDePago;
  const dateExpiredCreditCard = dayjs(paymentMethodFormValues?.expiration);
  const numberCreditCard = String(paymentMethodFormValues?.cardNumber);
  const paymentMethodExpirationMonth = dateExpiredCreditCard?.month() + 1;
  const paymentMethodExpirationYear = dateExpiredCreditCard?.year();

  const creditCardFiltered = listCreditCardInsured?.tarjetasDelAsegurado.find(
    (tarjeta) => tarjeta.numeroTarjeta === numberCreditCard
  );

  const sendClientData = useCallback(async () => {
    const {
      fullName,
      societyType,
      genderType,
      perceptionAgent,
      ivaCondition,
      address,
      documentNumber,
      documentType,
      cuit,
      nationality,
      inscriptionDate,
      inscriptionNumber,
      contractDate,
    } = clientDataSectionForm.getFieldsValue();

    const { cbuNumber, cardNumber, companyCreditCard } =
      paymentMethodSectionForm.getFieldsValue();

    const {
      profession,
      civilStatus,
      economicActivity,
      addressEmail,
      rutaNumber,
      cuilNumber,
      telephoneNumber,
      telephoneNumberJob,
      cellphoneNumber,
      webPage,
      birthDate,
      placeOfBirth,
      countryBirth,
    } = additionalDataSectionForm.getFieldsValue();

    const params: InsertInsuredPolicyholder = {
      numeroCotizacion: Number(quotationInfo!.token),
      codigoAsegurado: Number(quotationForm?.cliente?.nroAsegurado),
      nombreApellido: fullName,
      tipoDeSociedad: societyType,
      profesion: profession,
      genero: genderType,
      estadoCivil: civilStatus,
      actividadEconomica: economicActivity,
      mail: addressEmail,
      agenteDePercepcion: perceptionAgent,
      cbuDePago: cbuNumber,
      cbuParaSiniestros: cbuNumber,
      nroDeRuta: rutaNumber,
      inscripcionIva: ivaCondition,
      domicilio: {
        calleYNro: address,
        codigoPostal: Number(quotationForm?.cliente?.codigoPostal),
        sufijoPostal: Number(quotationForm?.cliente?.sufijoCodigoPostal),
      },
      documento: {
        tipoDocumento: documentType,
        numeroDocumento: documentNumber,
        cuit: cuit,
        cuil: cuilNumber,
      },
      telefonos: {
        particular: telephoneNumber,
        laboral: telephoneNumberJob,
        celular: cellphoneNumber,
        paginaWeb: webPage,
      },
      nacimiento: {
        fecha: birthDate ? dayjs(birthDate).format('DD-MM-YYYY') : "01-01-1999",
        lugar: placeOfBirth || '',
        codigoPais: countryBirth || 0,
        codigoNacionalidad: nationality || 0,
      },
      inscripcion: {
        fecha: dayjs(inscriptionDate).format('DD-MM-YYYY'),
        numero: inscriptionNumber,
        fechaDeContrato: dayjs(contractDate).format('DD-MM-YYYY'),
      },
    };

    if (cardNumber) {
      params.tarjetaDeCredito = {
        idEmpresa: companyCreditCard,
        numero: cardNumber,
        anioFin: paymentMethodExpirationYear,
        mesFin: paymentMethodExpirationMonth,
      };
    }

    try {
      await QuotationService.postInsertInsuredPolicyholder(params);
    } catch (error: any) {
      setErrorCreditCardMessage(error.message);
    }
  }, [
    clientDataSectionForm,
    additionalDataSectionForm,
    paymentMethodSectionForm,
    setErrorCreditCardMessage,
    quotationForm,
    quotationInfo,
    paymentMethodExpirationMonth,
    paymentMethodExpirationYear,
  ]);

  const sendProposal = useCallback(async (): Promise<number | undefined> => {
    const userName =
      authContext?.getUserInfo()?.intermediarioPrincipal?.nombreInter;

    const { startDate, endDate } = formattedDateVigency(
      stepTwoForm?.getFieldValue('vigencyDateRange')
    );

    const params: SendProposalRequest = {
      numeroCotizacion: Number(quotationInfo!.token),
      fechaDesde: startDate,
      fechaHasta: endDate,
      usuario: userName,
    };

    try {
      const response = await QuotationService.postSendProposal(params);
      const proposalNumber = response.data.numeroDePropuesta;

      setQuotationInfo((prev) => ({
        ...prev!,
        proposalNumber: proposalNumber,
      }));

      setCanShowConfirmIssue(true);
      return proposalNumber;
    } catch (error) { return undefined }
  }, [
    quotationInfo,
    authContext,
    stepTwoForm,
    setQuotationInfo,
    setCanShowConfirmIssue,
  ]);

  const sendPaymentMethod = useCallback(async () => {
    const { cbuNumber, cardNumber, companyCreditCard } =
      paymentMethodSectionForm.getFieldsValue();

    const creditCard = {
      idEmpresa: companyCreditCard ?? 0,
      numero: cardNumber || 0,
      anioFin: creditCardFiltered?.anioHasta ?? paymentMethodExpirationYear,
      mesFin: creditCardFiltered?.mesHasta ?? paymentMethodExpirationMonth,
    };

    const params: SendPaymentMethodRequest = {
      numeroCotizacion: Number(quotationInfo!.token),
      codigoFormaDePago: codeMethodPayment,
      numeroCbu: cbuNumber || 0,
      tarjetaDeCredito: creditCard,
    };

    try {
      await QuotationService.postInsertPaymentData(params);
    } catch (error: any) {
      paymentMethodSectionForm.setFields([{ name: 'cbuNumber', errors: [error.message] }]);
      throw error;
    }
  }, [
    paymentMethodSectionForm,
    creditCardFiltered,
    paymentMethodExpirationMonth,
    paymentMethodExpirationYear,
    codeMethodPayment,
    quotationInfo,
  ]);

  const sendInsertInsuredLifeObligatory = useCallback(async () => {
    try {
      const mappedInsuredList: AseguradosVida[] = insuredList.map((item) => {
        const cuilDigits = String(item.documentNumber ?? '').replace(/\D/g, '');
        return {
          nombre: item.fullName,
          cuil: cuilDigits ? Number(cuilDigits) : 0,
          fechaNacimiento: Number(dayjs(item.birthdayDate, 'DD/MM/YYYY').format('YYYYMMDD')),
          sexo: item.gender === 'M' ? 1 : 2,
        };
      });
      
      const params: any = {
        token: Number(quotationInfo?.token),
        rama: Number(quotationInfo?.branch),
        arse: Number(quotationInfo?.arse),
        personalDomestico: isPersonalDomestic() ? DECISION_TYPE.YES : DECISION_TYPE.NO,
        aseguradosVida: mappedInsuredList
      };

      await QuotationService.postInsertInsuredLifeObligatory(params);

    } catch (error) { }
  }, [
    quotationInfo,
    isPersonalDomestic,
    insuredList
  ]);

  const sendUploadFileInsured = useCallback(async (proposalNumber: number | undefined) => {
    try {
      if (uploadFile && uploadFile instanceof Blob) {
        const userInfo = authContext.userInfo;

        const extension = getFileExtension(uploadFile.type)
        const fileInsuredName = `NOMINA_${userInfo?.codigoDeEmpresa}_${userInfo?.codigoDeSucursal}_${quotationInfo?.token}_${userInfo?.intermediarioSecundario?.codigoInter}_${userInfo?.intermediarioSecundario?.nivelInter}_${proposalNumber}${extension}`;

        const base64 = await fileToBase64(uploadFile);

        const requestParams: DocumentUploadServiceRequestParams = {
          uploadDocument: {
            documentClass: DocumentClass.ObligatoryLifeInsured,
            fileName: fileInsuredName,
            fileType: uploadFile.type,
            base64: String(base64),
            applicationId: "WebProductores",
            author: userInfo!.usuarioInterno,
            attributes: [
              { id: "DocumentTitle", type: "String", value: fileInsuredName },
              { id: "cmis:name", type: "String", value: fileInsuredName },
              { id: "CodigoEmpresa", type: "String", value: userInfo?.codigoDeEmpresa ?? ''  },
              { id: "CodigoSucursal", type: "String", value: userInfo?.codigoDeSucursal ?? ''  },
              { id: "nctw", type: "String", value: quotationInfo?.token ?? ''  },
              { id: "CodigoProductor", type: "String", value: userInfo?.intermediarioSecundario?.codigoInter ?? ''  },
              { id: "CodigoLogeo", type: "String", value: userInfo?.intermediarioSecundario?.nivelInter ?? ''  },
              { id: "NumeroSolicitud", type: "String", value: String(proposalNumber) ?? '' },
            ],
          },
        };
        await ClaimService.sendClaimDocuments(requestParams);
      }

    } catch (error) { }
  },
    [
      authContext,
      quotationInfo,
      uploadFile,
    ]);


  const handleInsuredUpdate = (insuredList: any) => {
    setInsuredList(insuredList);
  };

  const handleUploadFileUpdate = (uploadFile: Blob | undefined) => {
    setUploadFile(uploadFile);
  };

  const handlerOnClick = useCallback(async () => {
    setCanShowSpinner(true);
    try {
      if(insuredList && insuredList.length <1){
         displayErrorMessage('Debe cargar al menos un asegurado.');
         return;
      }
      await clientDataSectionForm.validateFields();
      await paymentMethodSectionForm.validateFields();

      setclientDataFormValues(clientDataSectionForm.getFieldsValue());
      setPaymentMethodFormValues(paymentMethodSectionForm.getFieldsValue());

      if (isPersonalDomestic()) {
        await Promise.all([sendClientData(), sendPaymentMethod()]);
      } else {
        await sendClientData();
      }
       await sendInsertInsuredLifeObligatory()
       const proposalNumber = await sendProposal();
       await sendUploadFileInsured(proposalNumber);
    } catch (error) {
      message.error('Debe completar todos los campos');
    } finally {
      setCanShowSpinner(false);
    }
  }, [
    setclientDataFormValues,
    clientDataSectionForm,
    setPaymentMethodFormValues,
    paymentMethodSectionForm,
    setCanShowSpinner,
    sendProposal,
    sendClientData,
    sendPaymentMethod,
    sendInsertInsuredLifeObligatory,
    sendUploadFileInsured,
    isPersonalDomestic,
  ]);

  const handlerBackButtonOnClick = useCallback(() => {
    setclientDataFormValues(clientDataSectionForm.getFieldsValue());
    setAdditionalDataFormValues(additionalDataSectionForm.getFieldsValue());
    setPaymentMethodFormValues(paymentMethodSectionForm.getFieldsValue());
    setCurrentStep(QuotationStep.THREE);
  }, [
    setclientDataFormValues,
    clientDataSectionForm,
    setAdditionalDataFormValues,
    additionalDataSectionForm,
    setPaymentMethodFormValues,
    paymentMethodSectionForm,
    setCurrentStep,
  ]);

  useEffect(() => {
    if (errorCreditCardMessage) {
      message.error(errorCreditCardMessage);
      setErrorCreditCardMessage('');
    }
  }, [errorCreditCardMessage]);

  return (
    <>
      <ValiditySection
        stepTwoForm={stepTwoForm}
        validitySectionForm={validitySectionForm}
      />

      <ClientDataSection />

      <AdditionalDataSection />

      {isPersonalDomestic() ? (
        <PaymentMethodSection
          paymentMethodsForm={stepThreeForm}
          setMethodSelectedFromStepThree={setMethodSelectedFromStepThree}
        />
      ) : (
        <ContentSection level='h3' title='Método de pago'>
          <RowSize>
            <Typography.Text>
              Deberá abonar a través del formulario F931 de AFIP.
            </Typography.Text>
          </RowSize>
        </ContentSection>
      )}

      <Insured
        genderList={genderList}
        isLoadingGenderList={isLoadingGenderList}
        onInsuredChange={handleInsuredUpdate}
        onUploadFileChange={handleUploadFileUpdate}
      />

      <QuotationStepButtons
        label='Enviar'
        onClick={handlerOnClick}
        onPressBackButton={handlerBackButtonOnClick}
      />
    </>
  );
};
